"use client";

import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useAxios } from "@/app/providers/AxiosProvider";
import { useSession } from "next-auth/react";
import type { RecruitingSignal } from "./types";

export function useSignalsAccess() {
  const api = useAxios() as AxiosInstance;
  const { data: session } = useSession();
  const userId = session?.user?.id;
  const query = useQuery({
    queryKey: ["signals-access", userId],
    queryFn: async ({ signal }) => (await api.get<{ enabled: boolean; scope: string }>("/signals/access", { signal })).data,
    enabled: Boolean(userId), staleTime: 5 * 60_000, retry: false,
  });
  return { ...query, enabled: query.data?.enabled === true, scope: `${userId ?? ""}:${query.data?.scope ?? ""}` };
}

/**
 * When a refresh was last asked for per scope (the Refresh button, a finished research run).
 * The next fetch after it asks the API to skip its short feed cache.
 */
const refreshRequests = new Map<string, number>();

export function refreshSignals(client: QueryClient, scope: string) {
  refreshRequests.set(scope, Date.now());
  return client.invalidateQueries({ queryKey: ["signals", scope] });
}

export function useSignals(candidateId?: string | number) {
  const api = useAxios() as AxiosInstance;
  const client = useQueryClient();
  const access = useSignalsAccess();
  const queryKey = ["signals", access.scope, candidateId == null ? "all" : String(candidateId)];
  const feed = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const refresh = (refreshRequests.get(access.scope) ?? 0) > (client.getQueryState(queryKey)?.dataUpdatedAt ?? 0);
      const params = { ...(candidateId == null ? {} : { candidate_id: String(candidateId) }), ...(refresh ? { refresh: "1" } : {}) };
      return (await api.get<{ data: RecruitingSignal[] }>("/signals", { signal, params })).data.data;
    },
    enabled: access.enabled, staleTime: 60_000, retry: false,
  });
  const feedback = useMutation({
    mutationFn: async ({ id, value }: { id: string; value: -1 | 1 | null }) =>
      (await api.put<{ data: RecruitingSignal }>(`/signals/${id}/feedback`, { feedback: value })).data.data,
    onSuccess: (updated) => {
      client.setQueriesData<RecruitingSignal[]>({ queryKey: ["signals", access.scope] }, (items) =>
        items?.map((item) => item.id === updated.id ? updated : item));
    },
  });
  return { access, feed, feedback, refresh: () => refreshSignals(client, access.scope) };
}
